import type { Bet, BetBuilderGroup, BetBuilderRow, BettingSlipWithMonitoringInfo } from '../@types/api';

const createFallbackBetBuilderEvent = (group: BetBuilderGroup): Bet['event'] =>
  ({
    id: group.eventId,
    name: group.eventId,
    startDate: '',
    isLive: false,
    mainEventPartScore: null,
  }) as Bet['event'];

export const isBetBuilderTicket = (ticket?: Pick<BettingSlipWithMonitoringInfo, 'betBuilder'> | null) => {
  return Array.isArray(ticket?.betBuilder) && ticket.betBuilder.length > 0;
};

export const isBetBuilderRow = (row: Bet): row is BetBuilderRow => {
  return (row as BetBuilderRow).isBetBuilder === true;
};

export const betBuilderGroupRows = (groups?: BetBuilderGroup[] | null): BetBuilderRow[] => {
  if (!Array.isArray(groups) || groups.length === 0) return [];

  return groups.map((group) => {
    const selections = Array.isArray(group.selections) ? group.selections : [];
    const comboOdds = group.comboOdds ?? group.odds ?? 0;
    const selectionCount = selections.length;
    const event = (group.event as Bet['event'] | undefined) ?? createFallbackBetBuilderEvent(group);

    // Bet Builder groups don't map 1:1 to Bet — we build a synthetic row
    // that satisfies the Bet type so DataGrid can render it alongside normal bets.
    // Fields with no BB equivalent (outcomeId, banker, etc.) are placeholdered.
    const row: Bet = {
      id: `${group.id}`,
      outcomeId: '',
      odds: comboOdds,
      status: 'open',
      isLive: event.isLive ?? false,
      banker: false,
      icon: group.icon ?? '',
      ways: false,
      outcome: {
        createdAt: '',
        id: '',
        isActive: false,
        marketId: '',
        name: `${selectionCount} selection${selectionCount !== 1 ? 's' : ''}`,
        odds: comboOdds,
        outcomeTypeId: '',
        outcomeType: { id: '', name: '', shortName: '' },
        updatedAt: '',
        specialValues: '',
        latestChanges: null,
        latestTransferStatusChanges: null,
        resolveStatus: 'open',
        shortName: '',
      },
      market: {
        id: '',
        name: 'Bet Builder',
        marketTypeId: '',
        marketType: {
          id: '',
          sportId: '',
          name: 'Bet Builder',
          shortName: 'BB',
          isActive: true,
          eligibleForCashout: false,
          sport: {} as never,
          specialValueModelTypes: [],
          isSingleDisplayType: false,
          displayType: null,
          isCollapsedMode: false,
          usedFor: [],
          eventPartName: null,
          type: 'match',
          marketTypeCombiningIds: [],
        },
        eventId: group.eventId,
        position: null,
        isActive: true,
        createdAt: '',
        updatedAt: '',
        publishStatus: 'published',
        outcomes: [],
        specialValues: [],
        changed: false,
        latestChanges: null,
        isSingleDisplayType: false,
        isCollapsedMode: false,
        manualControl: false,
      },
      event,
      specialValues: [],
      mainEventPartScore: group.mainEventPartScore ?? event.mainEventPartScore ?? null,
      competitionName: group.competitionName ?? '',
      tournamentName: group.tournamentName ?? '',
      marketCombined: false,
    };

    return {
      ...row,
      isBetBuilder: true as const,
      groupId: group.id,
      selections,
    };
  });
};
